import { styled } from '@mui/material/styles';
import { Box, Container, IconButton, Modal } from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useModal } from '../../contexts/ModalContext';
import { ReactNode, useEffect, useRef } from 'react';
import { ThemeTypography } from '../common/ThemeTypography';
import { theme } from '../../configs/theme';
import { scrollModalContentToTop } from '../../utils/modal/scrollModalContentToTop';

export type BaseModalProps = {
  title?: ReactNode;
  subtitle?: ReactNode;
  name: string;
  children?: ReactNode;
  showCloseButton?: boolean;
  isLoading?: boolean;
  size?: 'small' | 'medium' | 'large';
  /** Overrides the width the size gives, for a modal whose content needs a fixed width. */
  width?: string;
};

export const BaseModal = ({
  title,
  subtitle,
  name,
  children,
  showCloseButton = true,
  isLoading = false,
  size = 'medium',
  width,
}: BaseModalProps) => {
  const { modalState, close } = useModal();
  const modalData = modalState[name] || { open: false, payload: null };
  const contentWrapperRef = useRef<HTMLDivElement | null>(null);

  const handleOnClose = () => {
    close(name);
  };

  useEffect(() => {
    const closeModalOnBackClickInBrowser = () => {
      close(name);
    };

    window.addEventListener('popstate', closeModalOnBackClickInBrowser);

    return () => {
      window.removeEventListener('popstate', closeModalOnBackClickInBrowser);
    };
  }, [close, name]);

  useEffect(() => {
    if (!modalData.open) {
      return;
    }

    scrollModalContentToTop();
  }, [modalData.open]);

  return (
    <ModalContainer open={modalData.open}>
      <ModalView size={size} role="dialog" aria-modal="true" sx={width ? { width } : undefined}>
        <ModalContent maxWidth={false}>
          <ModalHeader>
            {isLoading ? null : (
              <ThemeTypography
                type="heading1"
                color={theme.palette.customColors.emerald}
                sx={{ whiteSpace: 'pre-line' }}
              >
                {title}
              </ThemeTypography>
            )}
            {showCloseButton && (
              <FlexRowBox>
                <IconButton onClick={handleOnClose}>
                  <Close sx={{ width: 40, height: 40 }} />
                </IconButton>
              </FlexRowBox>
            )}
          </ModalHeader>
          <ModalHeader>
            <ThemeTypography
              type="body2"
              color={theme.palette.customColors.richBlack}
              sx={{ whiteSpace: 'pre-line' }}
            >
              {subtitle}
            </ThemeTypography>
          </ModalHeader>

          <ContentWrapper ref={contentWrapperRef} data-modal-scroll-container="true">
            {children}
          </ContentWrapper>
        </ModalContent>
      </ModalView>
    </ModalContainer>
  );
};

const ModalContainer = styled(Modal)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ModalView = styled(Box)<{ size?: 'small' | 'medium' | 'large' }>(
  ({ theme, size = 'large' }) => ({
    background: theme.palette.background.default,
    width: size === 'small' ? '40vw' : size === 'medium' ? '60vw' : '75vw',
    height: size === 'small' ? 'auto' : size === 'medium' ? '75vh' : '95vh',
    maxHeight: '95vh',
    borderRadius: '16px',
    padding: '30px',
    boxShadow: theme.shadows[5],
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: size === 'small' ? 'auto' : '90vh',
  })
);

const ModalContent = styled(Container)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  gap: '1rem',
  justifyContent: 'space-between',
}));

const ContentWrapper = styled(Box)(() => ({
  flex: 1,
  overflowY: 'auto',
  scrollbarWidth: 'none',
}));

const FlexRowBox = styled(Box)(() => ({
  display: 'flex',
  flexFlow: 'row',
  alignItems: 'center',
}));

const ModalHeader = styled(Box)((title) => ({
  display: 'flex',
  flexFlow: 'row',
  alignItems: 'center',
  justifyContent: `${title ? 'space-between' : 'end'}`,
  width: '100%',
}));
